import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, computed, makeObservable, observable } from 'mobx';

import type { AllEssayDto, EssayThemeDto } from '@/application/interfaces/pegasus.gateway';
import { Tracking } from '@/application/utils/tracking';
import { ApplicationModel } from '@/presentation/models/application.model';
import { UserModel } from '@/presentation/models/user.model';
import { EssayStore } from '@/presentation/stores/essay.store';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

import { EssayHighlightDto } from './../../../application/interfaces/pegasus.gateway';
import { EssayMediaInput } from './types';

@ViewModel({ singleton: true })
export class EssayViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable isTranscribing = false;
  @observable hasError = false;
  @observable essays: AllEssayDto;
  @observable essayThemes: EssayThemeDto[];
  @observable essayHighlight: EssayHighlightDto[];
  @observable isSendEssayMediaAgainModalOpen = false;
  @observable isEssayTypeModalOpen = false;
  @observable isEssayMediaModalOpen = false;
  @observable essayMediaFile: any;
  @observable isSending = false;

  constructor(
    private readonly applicationModel: ApplicationModel,
    private readonly userModel: UserModel,
    private readonly studentClassesStore: StudentClassesStore,
    private readonly essayStore: EssayStore,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    Tracking.pageView(this.studentClassesStore.getStudentClassesTrackingInfo());
    this.getContent();
    await this.essayStore.getStudentClassByClassifier();
  }

  @action.bound
  async getContent() {
    try {
      this.isLoading = true;
      this.getEssays();
      this.getThemes();
      this.getHighlights();
      this.hasError = false;
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    }
  }

  @action.bound
  async getEssays() {
    try {
      await this.essayStore.getAllEssays();
      this.essays = this.essayStore.essays;
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    }
  }

  @action.bound
  async getThemes() {
    try {
      await this.essayStore.getAllEssayThemes();
      this.essayThemes = this.essayStore.essayThemes;
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    }
  }

  @action.bound
  async getHighlights() {
    try {
      await this.essayStore.getAllEssayHighlights();
      this.essayHighlight = this.essayStore.essayHighlights;
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
    }
  }

  @action.bound
  setIsEssayTypeModalOpen(bool: boolean) {
    this.isEssayTypeModalOpen = bool;
  }

  @action.bound
  setIsEssayMediaModalOpen(bool: boolean) {
    this.isEssayMediaModalOpen = bool;
  }

  @action.bound
  setIsSendEssayMediaAgainModalOpen(bool: boolean) {
    this.isSendEssayMediaAgainModalOpen = bool;
  }

  @action.bound
  setIsTranscribing(bool: boolean) {
    this.isTranscribing = bool;
  }

  @action.bound
  async setEssayMediaFile(file: any) {
    this.essayMediaFile = file;
  }

  @action.bound
  async transcribeEssayMedia() {
    try {
      this.isSending = true;

      const essayMedia: EssayMediaInput = {
        file: this.essayMediaFile,
      };

      if (essayMedia.file) {
        await this.essayStore.createEssayMedia(essayMedia);
      }
    } catch (error) {
      this.isLoading = false;
      this.hasError = true;
      this.isSending = false;
    } finally {
      this.setIsTranscribing(false);
      this.setIsEssayMediaModalOpen(false);
    }
  }

  @computed
  get application() {
    return this.applicationModel.application;
  }

  @computed
  get user() {
    return this.userModel.user;
  }
}
